import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, Download, Eye, Building2, Search } from "lucide-react";
import { bondsAPI, formatDate } from "../../services/api";
import { createResponseSelector } from "../../utils/apiHelpers";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const STATUS_CONFIG = {
    Draft: { className: "bg-gray-100 text-gray-800", icon: "📝" },
    Approved: { className: "bg-green-100 text-green-800", icon: "✅" },
    Listed: { className: "bg-green-100 text-green-800", icon: "🏛️" },
    Active: { className: "bg-green-100 text-green-800", icon: "✅" },
    Matured: { className: "bg-blue-100 text-blue-800", icon: "🏁" },
};

const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { className: "bg-gray-100 text-gray-800", icon: "" };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
            {config.icon && <span className="mr-1">{config.icon}</span>}
            {status}
        </span>
    );
};

const PrimarySubscriberDocumentManagement = () => {
    const navigate = useNavigate();
    const [selectedSection, setSelectedSection] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const documentSections = [
        {
            id: "subscription-documents",
            title: "Subscription Documents",
            icon: FileText,
            description: "Manage subscription-related documents"
        },
        {
            id: "allocation-documents",
            title: "Allocation Documents",
            icon: FileText,
            description: "Access allocation and distribution records"
        }
    ];

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const { data: bondsData, isPending: bondsLoading } = useQuery({
        queryKey: ["subscriber-bonds", debouncedSearchTerm, statusFilter, currentPage],
        queryFn: () =>
            bondsAPI.getBonds({
                search: debouncedSearchTerm || undefined,
                status: statusFilter === "all" ? undefined : statusFilter,
                page: currentPage,
                limit: PAGE_SIZE,
            }),
        select: createResponseSelector("bonds", []),
    });

    const bonds = bondsData || [];

    const DocumentCard = ({ section }) => {
        const Icon = section.icon;

        return (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-all">
                <div className="flex items-start justify-between mb-3">
                    <Icon className="w-6 h-6 text-gray-600" />
                    <div className="flex gap-2">
                        <button className="p-1 rounded hover:bg-white/50" title="View">
                            <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-1 rounded hover:bg-white/50" title="Download">
                            <Download className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{section.title}</h3>
                <p className="text-xs text-gray-700">{section.description}</p>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Document Management
                            </h1>
                            <p className="text-sm text-gray-500">
                                Manage your subscription and allocation documents
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Bond Documents</h2>
                </div>

                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search bonds..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 min-w-[140px] whitespace-nowrap bg-white"
                    >
                        <option value="all">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Listed">Listed</option>
                        <option value="Approved">Approved</option>
                        <option value="Matured">Matured</option>
                    </select>
                </div>

                {bondsLoading ? (
                    <LoadingSpinner size="lg" text="Loading bonds..." />
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {bonds.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p>No bonds found</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                                {bonds.map((bond) => (
                                    <div
                                        key={bond._id}
                                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
                                        onClick={() => navigate(`/primary-subscriber/bonds/${bond._id}/documents`)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{bond.bondName}</h3>
                                                <p className="text-sm text-gray-500 mb-2">{bond.bondNumber}</p>
                                                <div className="flex flex-col gap-1 text-xs text-gray-500 mb-2">
                                                    <span>Issue: {bond.issueDate ? formatDate(bond.issueDate) : "N/A"}</span>
                                                    <span>Maturity: {bond.maturityDate ? formatDate(bond.maturityDate) : "N/A"}</span>
                                                </div>
                                                <div>
                                                    {getStatusBadge(bond.status)}
                                                </div>
                                            </div>
                                            <FileText className="h-5 w-5 text-gray-400" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PrimarySubscriberDocumentManagement;
